import {action, computed, makeObservable, observable} from 'mobx'
import {
    type OcGenericStepDialogStepConfiguration,
    OcGenericStepDialogStepConfigurationInput
} from './OcGenericStepDialogInterfaces'
import {
    OcGenericStepDialogButtonEnum,
    OcGenericStepDialogEvents,
    OcGenericStepDialogToggleButtonItem,
    type OcGenericStepDialogToggleButtonsEvent,
    type OcGenericStepDialogUpdateStepEvent
} from './OcGenericStepDialogEvents'
import {type OcGenericStepDialogUpdateConfigurationEvent} from './OcGenericStepDialogEvents'
import {type OcGenericStepDialogChangeStepEvent} from './OcGenericStepDialogEvents'
import {EventBusChannel} from '@oc/shell-core'

export class OcGenericStepDialogState {
    @observable public canGoNext = false
    @observable private stepsConfiguration: OcGenericStepDialogStepConfiguration[] = []
    @observable private _currentStep?: OcGenericStepDialogStepConfiguration = undefined
    @observable private _externalButtonSettings: Map<
        OcGenericStepDialogButtonEnum,
        Pick<OcGenericStepDialogToggleButtonItem, 'disabled' | 'hidden'>
    > = new Map()

    public constructor(
        private eventBusChannel: EventBusChannel<OcGenericStepDialogEvents>,
        public readonly dialogId: string
    ) {
        this.eventBusChannel = eventBusChannel
        makeObservable(this)
    }

    /** Info about currently selected step. */
    @computed
    get currentStep(): OcGenericStepDialogStepConfiguration | undefined {
        return this._currentStep
    }

    /** Index of current step in step configuration array (this.stepsConfiguration). */
    @computed
    get currentStepIndex(): number {
        if (typeof this._currentStep !== 'undefined') {
            return this._currentStep.sortId
        }
        return -1
    }

    /** Is current step first. */
    @computed
    get isFirstStep(): boolean {
        if (this._currentStep) {
            return this._currentStep.sortId === 0
        }
        return false
    }

    /** Is current step last. */
    @computed
    get isLastStep(): boolean {
        if (this._currentStep) {
            return this._currentStep.sortId === this.stepsConfiguration.length - 1
        }
        return false
    }

    /** Array of step labels (display names) in same order as steps. */
    @computed
    get stepLabels(): string[] {
        return this.stepsConfiguration.map(step => step.stepLabel)
    }

    @computed
    get externalButtonSettings() {
        return this._externalButtonSettings
    }

    /** Activate specified step, if the index exists in configuration. */
    @action.bound
    private setCurrentStepEvent(event: OcGenericStepDialogChangeStepEvent): void {
        const {stepIndex, dialogId} = event
        if (dialogId === this.dialogId) {
            this.setCurrentStep(stepIndex)
        }
    }

    /** Activate specified step, if the index exists in configuration. */
    @action.bound
    private setCurrentStep(stepIndex: number): void {
        if (stepIndex >= 0 && this.stepsConfiguration.length > stepIndex) {
            this._currentStep = this.stepsConfiguration[stepIndex]
            this._externalButtonSettings = new Map()
        }
    }

    /** Go to next step. */
    @action.bound
    public stepForward(): void {
        if (typeof this._currentStep !== 'undefined' && !this.isLastStep) {
            if (this._currentStep.disableNextOnForward) {
                this.canGoNext = false
            }

            if (this._currentStep.onForwardSideEffect) {
                this._currentStep.onForwardSideEffect()
            }

            this.setCurrentStep(this._currentStep.sortId + 1)
        }
    }

    /** Go to previous step. */
    @action.bound
    public stepBackward(): void {
        if (typeof this._currentStep !== 'undefined' && !this.isFirstStep) {
            if (this._currentStep.disableNextOnBackward) {
                this.canGoNext = false
            }

            if (this._currentStep.onBackwardSideEffect) {
                this._currentStep.onBackwardSideEffect()
            }

            this.setCurrentStep(this._currentStep.sortId - 1)
        }
    }

    /** Enable 'next' button. */
    @action.bound
    public enableGoNext(): void {
        this.canGoNext = true
    }

    /** Disable 'next' button. */
    @action.bound
    public disableGoNext(): void {
        this.canGoNext = false
    }

    /** Update step configuration and activate one of the steps (first or 'initialStepIndex'). Step activation can be prevented by 'skipStepActivation'. */
    @action.bound
    public updateStepsConfigurationEvent(event: OcGenericStepDialogUpdateConfigurationEvent): void {
        const {steps, initialStepIndex, dialogId, skipStepActivation, disableNextButton} = event
        if (dialogId === this.dialogId) {
            this.updateStepsConfiguration(steps, initialStepIndex, skipStepActivation, disableNextButton)
        }
    }

    /** Update step configuration and activate one of the steps (first or 'initialStepIndex'). Step activation can be prevented by 'skipStepActivation'. */
    @action.bound
    public updateStepsConfiguration(
        steps: OcGenericStepDialogStepConfigurationInput[],
        initialStepIndex?: number,
        skipStepActivation?: boolean,
        disableNextButton?: boolean
    ): void {
        const newSteps = steps.map((item, idx) => {
            return {
                ...item,
                sortId: idx
            } as OcGenericStepDialogStepConfiguration
        })
        this.stepsConfiguration = newSteps

        if (disableNextButton) {
            this.disableGoNext()
        }

        if (!skipStepActivation) {
            if (typeof initialStepIndex !== 'undefined') {
                this.setCurrentStep(initialStepIndex)
            } else {
                this.setCurrentStep(0)
            }
        }
    }

    @action.bound
    public dispose() {
        this._currentStep = undefined
        this.stepsConfiguration = []
        this.canGoNext = false
    }

    @action.bound
    private updateExternalButtonSettings(event: OcGenericStepDialogToggleButtonsEvent): void {
        if (event.dialogId === this.dialogId) {
            event.buttons.forEach(button => {
                this._externalButtonSettings.set(button.buttonType, {disabled: button.disabled, hidden: button.hidden})
            })
        }
    }

    @action.bound
    private updateStep(event: OcGenericStepDialogUpdateStepEvent): void {
        const {dialogId, stepIndex, propertiesToChange} = event
        const isActive = this._currentStep?.sortId === stepIndex

        if (dialogId === this.dialogId && stepIndex >= 0 && this.stepsConfiguration.length > stepIndex) {
            const newProps = Object.fromEntries(
                Object.entries(propertiesToChange).filter(
                    property => !['stepLabel', 'component'].includes(property[0]) || typeof property[1] !== 'undefined'
                )
            )

            this.stepsConfiguration[stepIndex] = {
                ...this.stepsConfiguration[stepIndex],
                ...newProps
            }

            if (isActive) {
                this._currentStep = this.stepsConfiguration[stepIndex]
            }
        }
    }

    public useOcGenericStepDialogChangeStepEvent() {
        this.eventBusChannel.useEvent('lib-components:generic-step-dialog:change-active-step', this.setCurrentStepEvent)
    }

    public useOcGenericStepDialogUpdateConfigurationEvent() {
        this.eventBusChannel.useEvent(
            'lib-components:generic-step-dialog:update-configuration',
            this.updateStepsConfigurationEvent
        )
    }

    public useOcGenericStepDialogToggleButtonsEvent() {
        this.eventBusChannel.useEvent(
            'lib-components:generic-step-dialog:toggle-buttons',
            this.updateExternalButtonSettings
        )
    }

    public useOcGenericStepDialogUpdateStepEvent() {
        this.eventBusChannel.useEvent('lib-components:generic-step-dialog:update-step', this.updateStep)
    }
}
